import {
  forwardRef,
  ComponentPropsWithoutRef,
  ReactElement,
  FocusEventHandler,
  useState,
} from "react";
import styles from "./Checkbox.module.scss";
import cx from "classnames";
import { LayoutUtilProps, FieldLabelProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";
import { Label } from "../Label";
import { SrOnly } from "../SrOnly";

/**
 * Props for the CheckboxGroup component
 * @extends ComponentPropsWithoutRef<"fieldset">
 * @extends LayoutUtilProps
 * @extends Omit<FieldLabelProps, "labelProps">
 */
export type CheckboxGroupProps = ComponentPropsWithoutRef<"fieldset"> &
  LayoutUtilProps &
  Omit<FieldLabelProps, "labelProps"> & {
    /**
     * required flag for label
     * @accessibility This does not enforce error when nothing is selected.
     * @default false
     */
    required?: boolean;

    /**
     * legend for the fieldset
     */
    legend: string | ReactElement;
  };

/**
 * CheckboxGroup component for grouping related checkboxes together.
 *
 * Features:
 * - Groups multiple checkboxes with a common legend
 * - Supports required field indication
 * - Optional helper text and descriptions
 * - Full accessibility support with fieldset/legend
 * - Focus management for keyboard navigation
 * - Layout utility props for positioning and spacing
 * - Flexible layout direction (row/column)
 * - Screen reader support with hidden labels
 *
 * @example
 * <CheckboxGroup legend="Select your interests" required>
 *   <Checkbox label="Technology" />
 *   <Checkbox label="Sports" />
 *   <Checkbox label="Music" />
 * </CheckboxGroup>
 */
export const CheckboxGroup = forwardRef<
  HTMLFieldSetElement,
  CheckboxGroupProps
>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);

  const {
    className,
    children,
    legend,
    moreInfo,
    onBlur,
    onFocus,
    required,
    style,
    ...rest
  } = componentProps;

  const checkboxGroupClassNames = cx(styles["checkbox-group"], className);
  const [openInfo, setOpenInfo] = useState<boolean>(false);

  const styleCombined = {
    ...style,
    ...layoutStyles,
    alignItems:
      layoutStyles?.alignItems ??
      (layoutStyles?.flexDirection === "row" ? "flex-start" : undefined),
  };

  const focusHandler: FocusEventHandler<HTMLFieldSetElement> = (e) => {
    onFocus?.(e);
    setOpenInfo(true);
  };

  const blurHandler: FocusEventHandler<HTMLFieldSetElement> = (e) => {
    onBlur?.(e);
    setOpenInfo(false);
  };

  const labelClassNames = cx(styles["label"], {
    [styles["direction-row"]]: layoutStyles?.flexDirection === "row",
  });

  return (
    <fieldset
      role="group"
      data-anv="checkbox-group"
      className={checkboxGroupClassNames}
      ref={ref}
      style={styleCombined}
      onFocus={focusHandler}
      onBlur={blurHandler}
      {...rest}
    >
      <legend className={styles["legend"]}>
        {legend}
        {required && ", required"}
        {moreInfo && <SrOnly>{moreInfo}</SrOnly>}
      </legend>
      <Label
        aria-hidden
        moreInfo={moreInfo}
        required={required}
        openMoreInfo={openInfo}
        className={labelClassNames}
      >
        {legend}
      </Label>
      <div
        className={styles["children"]}
        style={{
          flexDirection: layoutStyles?.flexDirection,
          gap: layoutStyles?.gap,
        }}
      >
        {children}
      </div>
    </fieldset>
  );
});

CheckboxGroup.displayName = "CheckboxGroup";
